import { useEffect, useRef, useState } from 'react';
import LogoLockup from '../../chambers/LogoLockup';
import GoogleGlyph from './GoogleGlyph';
import {
  CTA,
  NAV_EXPLORE,
  NAV_LINKS,
  NAV_PRODUCT_LABEL,
  PRACTICES,
  STATUS_LABEL,
  type Practice,
} from './homepageContent';

/**
 * Logged-out header — compact and sticky. Product is the only grouped item (the three
 * practices with their status, then Explore Product); Pricing, FAQ, Trust Center and Contact
 * Us are direct destinations — the last two open the Trust Center slide view over the page
 * (TrustCenterSlide). Start Free Trial is the one primary action; Sign in with Google is
 * the lighter secondary one. Below lg everything but the trial button moves into a menu.
 *
 * Both menus are disclosures, not ARIA menus: they hold ordinary links, close on Escape
 * (focus returns to their button), on a click outside, and once a link is followed.
 */

interface LandingNavProps {
  onLogin: () => void;
  loading: boolean;
}

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-brass focus-visible:outline-offset-2';

function StatusBadge({ practice }: { practice: Practice }) {
  const live = practice.status === 'beta_live';
  return (
    <span
      className={`font-plex text-[9.5px] tracking-[0.12em] uppercase px-1.5 py-0.5 whitespace-nowrap ${
        live ? 'bg-brass text-chamber-ink' : 'border border-chamber-border-hi text-chamber-muted'
      }`}
    >
      {STATUS_LABEL[practice.status]}
    </span>
  );
}

function ProductItems({ onPick }: { onPick: () => void }) {
  return (
    <>
      {PRACTICES.map((p) => (
        <li key={p.key}>
          <a
            href={`#practice-${p.key}`}
            onClick={onPick}
            className={`flex items-center justify-between gap-4 px-4 py-2.5 text-[14px] text-parchment hover:bg-chamber-card hover:text-brass transition-colors ${FOCUS}`}
          >
            {p.name}
            <StatusBadge practice={p} />
          </a>
        </li>
      ))}
      <li className="border-t border-brass-hairline-faint mt-1 pt-1">
        <a
          href={NAV_EXPLORE.href}
          onClick={onPick}
          className={`block px-4 py-2.5 text-[14px] text-brass hover:bg-chamber-card transition-colors ${FOCUS}`}
        >
          {NAV_EXPLORE.label} →
        </a>
      </li>
    </>
  );
}

export default function LandingNav({ onLogin, loading }: LandingNavProps) {
  const [productOpen, setProductOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const productRef = useRef<HTMLDivElement>(null);
  const productButtonRef = useRef<HTMLButtonElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Escape closes whichever menu is open and hands focus back to its button; a click
  // outside the header closes both.
  useEffect(() => {
    if (!productOpen && !mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (productOpen) {
        setProductOpen(false);
        productButtonRef.current?.focus();
      }
      if (mobileOpen) {
        setMobileOpen(false);
        mobileButtonRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (productOpen && !productRef.current?.contains(target)) setProductOpen(false);
      if (mobileOpen && !headerRef.current?.contains(target)) setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [productOpen, mobileOpen]);

  const closeAll = () => {
    setProductOpen(false);
    setMobileOpen(false);
  };

  const navLink = `font-instrument text-[13px] font-medium tracking-[0.06em] text-chamber-muted hover:text-brass transition-colors ${FOCUS}`;

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-40 border-b border-brass-hairline bg-chamber-rail/95 backdrop-blur-sm"
    >
      <nav
        aria-label="Main"
        className="flex items-center justify-between gap-3 px-5 sm:px-8 lg:px-14 py-2 sm:py-2.5"
      >
        <a href="#top" aria-label="iAdvocate — back to top" className={`flex shrink-0 ${FOCUS}`}>
          {/* Smaller lockup on phones so it, the trial button and the menu fit 360px. */}
          <LogoLockup
            markClass="h-[24px] sm:h-[28px] w-auto text-brass"
            textClass="font-display text-[19px] sm:text-[22px] leading-none tracking-[0.01em] text-parchment"
          />
        </a>

        {/* Five items from 2026-11-03 (Contact Us): a tighter gap until xl keeps 1024px airy. */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          <div
            ref={productRef}
            className="relative"
            onBlur={(e) => {
              // Tabbing out of the open menu closes it.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setProductOpen(false);
            }}
          >
            <button
              ref={productButtonRef}
              type="button"
              aria-expanded={productOpen}
              aria-controls="product-menu"
              onClick={() => setProductOpen((o) => !o)}
              className={`${navLink} flex items-center gap-1.5 cursor-pointer`}
            >
              {NAV_PRODUCT_LABEL}
              <svg
                aria-hidden="true"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                className={`transition-transform ${productOpen ? 'rotate-180' : ''}`}
              >
                <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </button>
            {productOpen && (
              <ul
                id="product-menu"
                className="absolute left-0 top-full mt-3 w-[320px] border border-chamber-border-mid bg-chamber-panel py-1.5 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.7)]"
              >
                <ProductItems onPick={closeAll} />
              </ul>
            )}
          </div>
          {NAV_LINKS.map((link) => (
            <a key={link.label} href={link.href} className={navLink}>
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2.5 sm:gap-5">
          <button
            type="button"
            onClick={onLogin}
            disabled={loading}
            className={`hidden lg:flex items-center gap-2 font-instrument text-[12.5px] text-chamber-muted hover:text-brass transition-colors disabled:opacity-50 cursor-pointer ${FOCUS}`}
          >
            <GoogleGlyph />
            {CTA.signIn}
          </button>
          <button
            type="button"
            onClick={onLogin}
            disabled={loading}
            className={`bg-brass text-chamber-ink px-3 sm:px-5 py-2 text-[11.5px] sm:text-[12px] font-semibold tracking-[0.1em] uppercase whitespace-nowrap hover:bg-brass-hover transition-colors disabled:opacity-50 cursor-pointer ${FOCUS}`}
          >
            {CTA.trial}
          </button>
          <button
            ref={mobileButtonRef}
            type="button"
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen((o) => !o)}
            className={`lg:hidden flex h-9 w-9 items-center justify-center border border-chamber-border-hi text-parchment cursor-pointer ${FOCUS}`}
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18">
              {mobileOpen ? (
                <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.6" />
              ) : (
                <path d="M2 5h14M2 9h14M2 13h14" stroke="currentColor" strokeWidth="1.6" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div id="mobile-menu" className="lg:hidden border-t border-brass-hairline-soft bg-chamber-panel pb-4">
          <p className="px-5 sm:px-8 pt-4 pb-1 font-plex text-[10.5px] tracking-[0.24em] text-chamber-muted-dim uppercase">
            {NAV_PRODUCT_LABEL}
          </p>
          <ul className="px-1 sm:px-4">
            <ProductItems onPick={closeAll} />
          </ul>
          <ul className="mt-2 border-t border-brass-hairline-faint px-1 sm:px-4 pt-2">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  onClick={closeAll}
                  className={`block px-4 py-2.5 text-[15px] text-parchment hover:text-brass ${FOCUS}`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="px-5 sm:px-8 pt-3">
            <button
              type="button"
              onClick={() => {
                closeAll();
                onLogin();
              }}
              disabled={loading}
              className={`flex items-center gap-2.5 text-[14px] text-chamber-muted hover:text-brass disabled:opacity-50 cursor-pointer ${FOCUS}`}
            >
              <GoogleGlyph />
              {CTA.signIn}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
